// =====================================================================
// EGL v12 Frontend - USER PASSWORD MANAGER COMPONENT
// Purpose: Admin interface for managing user passwords
// Added: 2025-11-18
// Security: Admin-only access, audit logging
// =====================================================================

import React, { useState, useEffect } from 'react';
import './UserPasswordManager.css';

interface User {
  id: string;
  username: string;
  email: string;
  firstname: string;
  lastname: string;
  role: string;
  status: string;
  is_active: boolean;
  last_login_at?: string;
}

export const UserPasswordManager: React.FC = () => {
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [showResetModal, setShowResetModal] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [generatedPassword, setGeneratedPassword] = useState('');
  const [statusMessage, setStatusMessage] = useState<{text: string, type: 'success' | 'error' | 'info'} | null>(null);
  
  // Fetch all users
  const fetchUsers = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('egl_token');
      const response = await fetch('/api/auth/admin/users/all', {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      const data = await response.json();
      if (data.success) {
        setUsers(data.data.users);
      } else {
        console.error('Failed to fetch users:', data.message);
      }
    } catch (error) {
      console.error('Error fetching users:', error);
    } finally {
      setLoading(false);
    }
  };
  
  // Generate strong password
  const generateStrongPassword = async () => {
    try {
      const token = localStorage.getItem('egl_token');
      const response = await fetch('/api/auth/admin/users/generate-password', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      const data = await response.json();
      if (data.success) {
        const pwd = data.data.generated_password;
        setNewPassword(pwd);
        setConfirmPassword(pwd);
        setGeneratedPassword(pwd);
        setStatusMessage({ text: '🎲 Strong password generated!', type: 'success' });
        setTimeout(() => setStatusMessage(null), 3000);
      } else {
        setStatusMessage({ text: `Error: ${data.message}`, type: 'error' });
        setTimeout(() => setStatusMessage(null), 5000);
      }
    } catch (error) {
      console.error('Error generating password:', error);
      setStatusMessage({ text: 'Failed to generate password', type: 'error' });
      setTimeout(() => setStatusMessage(null), 5000);
    }
  };
  
  // Reset password
  const resetPassword = async () => {
    if (!selectedUser) return;
    
    // Validation
    if (newPassword.length < 8) {
      setStatusMessage({ text: '❌ Password must be at least 8 characters', type: 'error' });
      setTimeout(() => setStatusMessage(null), 5000);
      return;
    }
    
    if (newPassword !== confirmPassword) {
      setStatusMessage({ text: '❌ Passwords do not match', type: 'error' });
      setTimeout(() => setStatusMessage(null), 5000);
      return;
    }
    
    try {
      const token = localStorage.getItem('egl_token');
      const response = await fetch(
        `/api/auth/admin/users/${selectedUser.id}/reset-password`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ newPassword })
        }
      );
      
      const data = await response.json();
      if (data.success) {
        setStatusMessage({ 
          text: `✅ Password reset successfully for ${selectedUser.username}`, 
          type: 'success' 
        });
        setShowResetModal(false);
        resetForm();
        setTimeout(() => setStatusMessage(null), 5000);
      } else {
        setStatusMessage({ text: `❌ Error: ${data.message}`, type: 'error' });
        setTimeout(() => setStatusMessage(null), 5000);
      }
    } catch (error) {
      console.error('Error resetting password:', error);
      setStatusMessage({ text: '❌ Failed to reset password', type: 'error' });
      setTimeout(() => setStatusMessage(null), 5000);
    }
  };
  
  // Reset form
  const resetForm = () => {
    setSelectedUser(null);
    setNewPassword('');
    setConfirmPassword('');
    setGeneratedPassword('');
    setShowPassword(false);
  };
  
  // Copy to clipboard
  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    setStatusMessage({ text: '📋 Password copied to clipboard!', type: 'info' });
    setTimeout(() => setStatusMessage(null), 3000);
  };
  
  // Calculate password strength
  const getPasswordStrength = (password: string): {level: 'weak' | 'medium' | 'strong', label: string} => {
    if (password.length === 0) return {level: 'weak', label: ''};
    if (password.length < 8) return {level: 'weak', label: '❌ Too Short'};
    if (password.length < 12) return {level: 'medium', label: '⚠️ Medium'};
    return {level: 'strong', label: '✅ Strong'};
  };
  
  useEffect(() => {
    fetchUsers();
    
    // Real-time updates (poll every 30 seconds)
    const interval = setInterval(fetchUsers, 30000);
    return () => clearInterval(interval);
  }, []);
  
  const passwordStrength = getPasswordStrength(newPassword);
  
  return (
    <div className="password-manager">
      <div className="password-manager-header">
        <h3>🔐 User Password Management</h3>
        <button 
          className="refresh-btn"
          onClick={fetchUsers}
          disabled={loading}
        >
          {loading ? '⏳ Loading...' : '🔄 Refresh'}
        </button>
      </div>
      
      {/* Status Message */}
      {statusMessage && (
        <div className={`status-message ${statusMessage.type}`}>
          {statusMessage.text}
        </div>
      )}
      
      {/* User List */}
      <div className="user-list">
        {loading && users.length === 0 ? (
          <div className="loading-state">Loading users...</div>
        ) : (
          <table>
            <thead>
              <tr>
                <th>Username</th>
                <th>Email</th>
                <th>Name</th>
                <th>Role</th>
                <th>Status</th>
                <th>Last Login</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {users.map(user => (
                <tr key={user.id}>
                  <td><strong>{user.username}</strong></td>
                  <td>{user.email}</td>
                  <td>{user.firstname} {user.lastname}</td>
                  <td>
                    <span className={`role-badge ${user.role.toLowerCase()}`}>
                      {user.role}
                    </span>
                  </td>
                  <td>
                    <span className={`status-badge ${user.status.toLowerCase()}`}>
                      {user.status}
                    </span>
                  </td>
                  <td className="last-login">
                    {user.last_login_at 
                      ? new Date(user.last_login_at).toLocaleString()
                      : 'Never'}
                  </td>
                  <td>
                    <button
                      className="reset-btn"
                      onClick={() => {
                        setSelectedUser(user);
                        setShowResetModal(true);
                      }}
                    >
                      🔄 Reset Password
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
      
      {/* Reset Password Modal */}
      {showResetModal && selectedUser && (
        <div className="modal-overlay" onClick={() => {
          setShowResetModal(false);
          resetForm();
        }}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Reset Password for {selectedUser.username}</h3>
              <button 
                className="close-btn"
                onClick={() => {
                  setShowResetModal(false);
                  resetForm();
                }}
              >
                ✕
              </button>
            </div>
            
            <div className="modal-body">
              <div className="form-group">
                <label>New Password</label>
                <div className="password-input-group">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    placeholder="Enter new password (min 8 characters)"
                    autoFocus
                  />
                  <button
                    className="toggle-password"
                    onClick={() => setShowPassword(!showPassword)}
                    type="button"
                  >
                    {showPassword ? '👁️' : '👁️‍🗨️'}
                  </button>
                </div>
              </div>
              
              <div className="form-group">
                <label>Confirm Password</label>
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Confirm new password"
                />
              </div>
              
              <div className="password-actions">
                <button
                  className="generate-btn"
                  onClick={generateStrongPassword}
                  type="button"
                >
                  🎲 Generate Strong Password
                </button>
                
                {generatedPassword && (
                  <button
                    className="copy-btn"
                    onClick={() => copyToClipboard(generatedPassword)}
                    type="button"
                  >
                    📋 Copy to Clipboard
                  </button>
                )}
              </div>
              
              {newPassword.length > 0 && (
                <div className="password-strength">
                  <div className={`strength-indicator ${passwordStrength.level}`}>
                    {passwordStrength.label}
                  </div>
                  <div className="strength-requirements">
                    <div className={newPassword.length >= 8 ? 'met' : 'unmet'}>
                      {newPassword.length >= 8 ? '✓' : '○'} At least 8 characters
                    </div>
                    <div className={newPassword.length >= 12 ? 'met' : 'unmet'}>
                      {newPassword.length >= 12 ? '✓' : '○'} 12+ characters (recommended)
                    </div>
                  </div>
                </div>
              )}
              
              <div className="warning-box">
                <strong>⚠️ Warning:</strong> User will be logged out immediately after password reset.
              </div>
            </div>
            
            <div className="modal-footer">
              <button
                className="cancel-btn"
                onClick={() => {
                  setShowResetModal(false);
                  resetForm();
                }}
                type="button"
              >
                Cancel
              </button>
              <button
                className="confirm-btn"
                onClick={resetPassword}
                disabled={!newPassword || newPassword !== confirmPassword || newPassword.length < 8}
                type="button"
              >
                🔐 Reset Password
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

